import IpInfoApi from "@/api/IpInfoApi";
import SecurityApi from "@/api/SecurityApi";

import TokenUseCase from "./TokenUseCase";
import LogoutTimer from "./LogoutTimer";
import store from "@/store";

export default class SecurityUseCase {
  private readonly _securityApi: SecurityApi;
  private readonly _tokenManager: TokenUseCase;

  constructor(securityApi: SecurityApi = new SecurityApi()) {
    this._securityApi = securityApi;
    this._tokenManager = new TokenUseCase();
  }

  async login(emailAddress: string, password: string) {
    try {
      const ipInfoApi = new IpInfoApi();

      const ipInfo = await ipInfoApi.getIpInfo();
      ipInfo.device = this.getDevice();

      const result = await this._securityApi.login({
        emailAddress,
        password,
        ...ipInfo,
      });

      if (result.content) {
        await this._tokenManager.setAccessToken(result.content);
      }
      return result;
    } catch (error) {
      throw error;
    }
  }

  async requestPasswordReset(emailAddress: string) {
    try {
      const result = await this._securityApi.requestPasswordReset(emailAddress);
      return result;
    } catch (error) {
      throw error;
    }
  }

  async resetPassword(token: string, emailAddress: string, password: string) {
    try {
      const result = await this._securityApi.resetPassword(
        token,
        emailAddress,
        password,
      );
      return result;
    } catch (error) {
      throw error;
    }
  }

  async getSMSVerificationCode(token: string, userReference: string) {
    try {
      const result = await this._securityApi.getSMSVerificationCode(
        token,
        userReference,
      );
      if (result.content)
        await this._tokenManager.setAccessToken(result.content.mfaToken);
      return result;
    } catch (error) {
      throw error;
    }
  }

  async verifyMFAForLogin(verificationCode: string) {
    try {
      const result =
        await this._securityApi.verifyMFAForLogin(verificationCode);
      if (result.content) {
        await this._tokenManager.setAccessToken(result.content.accessToken);
        store.commit("mutStoreCurrentUser", result.content.user);
      }
      return result;
    } catch (error) {
      throw error;
    }
  }

  async updateToken() {
    try {
      const response = await this._securityApi.updateToken();
      if (response.content) {
        await this._tokenManager.setAccessToken(response.content.accessToken);

        const logoutTime = await this._tokenManager.getTokenExpiryTime();
        if (response.status !== 200) {
          await this._tokenManager.clearAccessToken();
          store.commit("mutClearCurrentUser");
        } else if (logoutTime) {
          store.commit("mutStoreCurrentUser", response.content.user);
          let timer = new LogoutTimer();
          /**
           * logoutTime is in UTC second
           * So we subtract the current UTC time in seconds
           * This gives the remaining time in seconds
           * Floor to get rid of decimal places. Logout early than late.
           */
          const logoutCountDown = Math.floor(logoutTime - Date.now() / 1000);
          if (logoutCountDown > 0) timer.resetTimer();
          else {
            await new TokenUseCase().clearAccessToken();
            store.commit("mutClearCurrentUser");
          }
          // timer.resetTimer();
        }
      }

      return response;
    } catch (error) {
      await this._tokenManager.clearAccessToken();
      store.commit("mutClearCurrentUser");
      throw error;
    }
  }

  async logout() {
    try {
      const result = await this._securityApi.logout();

      return result;
    } catch (error) {
      throw error;
    } finally {
      await this._tokenManager.clearAccessToken();
      // Clear the logout time in seconds
      store.commit("mutStoreLogoutTimeInSeconds", undefined);
      // clear the logged user in store
      store.commit("mutClearCurrentUser");

      // To clear the timer if restarted by the reponsehandler
      LogoutTimer.clearTimer();
    }
  }

  async doesTokenExist() {
    try {
      return await this._tokenManager.doesAccessTokenExist();
    } catch (error) {
      throw error;
    }
  }

  async getUserPermissions() {
    try {
      return await this._tokenManager.getUserPermissionsAsync();
    } catch (error) {
      throw error;
    }
  }

  async isAccessToken() {
    try {
      return await this._tokenManager.isAccessToken();
    } catch (error) {
      throw error;
    }
  }

  async sendEmailVerificationChallengeCode(
    mobilePhoneNumber: string,
    emailAddress: string,
    givenName: string,
    familyName: string,
  ) {
    try {
      const result = await this._securityApi.sendEmailVerificationChallengeCode(
        mobilePhoneNumber,
        emailAddress,
        givenName,
        familyName,
      );
      return result;
    } catch (error) {
      throw error;
    }
  }

  async sendMobilehoneNumberVerificationChallengeCode(
    mobilePhoneNumber: string,
    emailAddress: string,
    givenName: string,
    familyName: string,
  ) {
    try {
      const result =
        await this._securityApi.sendMobilehoneNumberVerificationChallengeCode(
          mobilePhoneNumber,
          emailAddress,
          givenName,
          familyName,
        );
      return result;
    } catch (error) {
      throw error;
    }
  }

  async verifyEmailAddressChallengeCode(
    emailAddress: string,
    challengeCode: string,
  ) {
    try {
      const result = await this._securityApi.verifyEmailAddressChallengeCode(
        emailAddress,
        challengeCode,
      );
      return result;
    } catch (error) {
      throw error;
    }
  }

  async verifyMobilePhoneNumberChallengeCode(
    mobilePhoneNumber: string,
    emailAddress: string,
    challengeCode: string,
  ) {
    try {
      const result =
        await this._securityApi.verifyMobilePhoneNumberChallengeCode(
          mobilePhoneNumber,
          emailAddress,
          challengeCode,
        );
      return result;
    } catch (error) {
      throw error;
    }
  }

  async isEasilyGuessedPassword(password: string) {
    try {
      const result = await this._securityApi.isEasilyGuessedPassword(password);
      return result;
    } catch (error) {
      throw error;
    }
  }

  async getUserPermissionsList() {
    try {
      const result = await this._securityApi.getUserPermissionsList();
      return result;
    } catch (error) {
      throw error;
    }
  }

  private getDevice() {
    let userAgentInfo = navigator.userAgent;
    if (
      /(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgentInfo)
    ) {
      return "Tablet";
    }
    if (
      /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
        userAgentInfo,
      )
    ) {
      return "Mobile";
    }
    return "Desktop Web";
  }
}
